import { useEffect } from "react";
import * as THREE from "three";
import { CSS2DObject } from "three-stdlib";
import { resolveJointUuidFromRef } from "../../utils/jointUtils";
import { useSelectionStore } from "../../stores/selectionStore";
import {
  applyJointLabelLayoutStyles,
  applyLabelStyles,
  JOINT_LABEL_ACTUATOR_ICON_CLASS,
  JOINT_LABEL_TEXT_CLASS,
} from "../labelUtils";
import {
  applyMarkerLabelCss2dLayout,
  CONSTRAINT_MARKER_LABEL_LIFT_LOCAL_METERS,
} from "../coincidentLabelSpread";
import { disposeSceneObjectTree } from "../scene/sceneDispose";
import {
  buildViewerSceneRouteObjectIndex,
  collectSpatialTendonRouteObjects,
  findJointVizGroupInScene,
} from "../scene/sceneObjectLookups";
import { computeConstraintMarkerScaleFromModelGroup } from "../scene/jointMarkerSizing";
import { setJointPivotWorldFromViz } from "../scene/constraintMarkerGeometry";
import { ACTUATED_TENDON_COLOR, CONSTRAINT_MARKER_RADIUS } from "../scene/sceneConstants";

const ACTUATOR_MARKER_GROUP_NAME = "__actuatorMarkers";
const ACTUATOR_LABEL_CLASS = "actuator-marker-label";
const ACTUATOR_MARKER_OFFSET_METERS = 0.018;

function stripLocalPrefix(name) {
  if (!name) return "";
  const raw = String(name);
  const slash = raw.lastIndexOf("/");
  return slash >= 0 ? raw.slice(slash + 1) : raw;
}

function resolveTendonForActuator(actuator, tendons) {
  if (!actuator || !tendons) return null;
  const tendonUuid = typeof actuator.tendonUuid === "string" ? actuator.tendonUuid : "";
  if (tendonUuid && tendons[tendonUuid]) return tendons[tendonUuid];
  const tendonRef = typeof actuator.tendon === "string" ? actuator.tendon : "";
  if (!tendonRef) return null;
  return (
    tendons[tendonRef] ||
    Object.values(tendons).find(
      (tendon) => tendon?.uuid === tendonRef || tendon?.name === tendonRef
    ) ||
    null
  );
}

function createActuatorLabelElement({ actuator, labelsVisible, onSelect }) {
  const labelText = stripLocalPrefix(actuator?.name) || actuator?.kind || "Actuator";
  const labelDiv = document.createElement("div");
  labelDiv.className = ACTUATOR_LABEL_CLASS;
  labelDiv.title = labelText;
  applyLabelStyles(labelDiv);
  applyJointLabelLayoutStyles(labelDiv);
  labelDiv.style.setProperty("pointer-events", "auto", "important");

  const iconWrap = document.createElement("span");
  iconWrap.className = JOINT_LABEL_ACTUATOR_ICON_CLASS;
  iconWrap.setAttribute("aria-hidden", "true");
  iconWrap.style.setProperty("display", "inline-flex", "important");
  iconWrap.style.setProperty("color", "#d97706", "important");
  iconWrap.style.setProperty("align-items", "center", "important");
  iconWrap.style.setProperty("justify-content", "center", "important");
  iconWrap.style.setProperty("flex-shrink", "0", "important");
  iconWrap.style.setProperty("margin-inline-end", labelsVisible ? "4px" : "0", "important");
  iconWrap.style.lineHeight = "0";
  iconWrap.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 14a1 1 0 0 1-.78-1.63l9.4-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.4 10.2a.5.5 0 0 1-.86-.46l-1.92-6.02A1 1 0 0 0 11 14z"/></svg>';

  labelDiv.appendChild(iconWrap);

  if (labelsVisible) {
    const textSpan = document.createElement("span");
    textSpan.className = JOINT_LABEL_TEXT_CLASS;
    textSpan.textContent = labelText;
    labelDiv.appendChild(textSpan);
  }

  labelDiv.addEventListener("click", onSelect);
  labelDiv.cleanupActuatorLabel = () => labelDiv.removeEventListener("click", onSelect);
  return labelDiv;
}

function cleanupActuatorMarkerGroup(group) {
  if (!group) return;
  group.traverse((obj) => {
    if (obj?.isCSS2DObject && obj.element) {
      obj.element.cleanupActuatorLabel?.();
      obj.element.remove?.();
    }
  });
  while (group.children.length > 0) {
    const child = group.children[0];
    disposeSceneObjectTree(child);
    group.remove(child);
  }
}

function createMarkerSphere(radius) {
  return new THREE.Mesh(
    new THREE.SphereGeometry(radius, 16, 16),
    new THREE.MeshBasicMaterial({
      color: ACTUATED_TENDON_COLOR,
      transparent: true,
      opacity: 0.96,
      depthTest: false,
      depthWrite: false,
    })
  );
}

function getTendonAnchor(tendon, scene, routeObjectIndex) {
  if (!tendon) return null;
  if (tendon.kind === "spatial") {
    const routeObjects = collectSpatialTendonRouteObjects(scene, tendon, routeObjectIndex);
    const routeObject = routeObjects[0] || null;
    if (routeObject) return { kind: "route", routeObject };
  }
  const jointRef = (tendon.joints || []).find((entry) => entry?.joint)?.joint;
  if (!jointRef) return null;
  const vizGroup = findJointVizGroupInScene(scene, jointRef);
  return vizGroup ? { kind: "joint", vizGroup } : null;
}

function updateMarkerPosition(marker, jointObjectsRef) {
  const ud = marker?.userData || {};
  if (ud.anchorKind === "joint" && ud.vizGroup) {
    setJointPivotWorldFromViz(marker.position, ud.vizGroup, jointObjectsRef);
  } else if (ud.anchorKind === "route" && ud.routeObject) {
    ud.routeObject.getWorldPosition(marker.position);
  }
  if (ud.anchorOffsetIndex) {
    marker.position.y += ud.anchorOffsetIndex * ACTUATOR_MARKER_OFFSET_METERS;
  }
}

export function useActuatorMarkers({
  actuators,
  allTendons,
  jointObjectsRef,
  labelsVisible,
  safeJointMapObject,
  sceneObjects,
  sceneRef,
  showActuators,
  structureChangeTick,
}) {
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return undefined;

    let group = scene.getObjectByName(ACTUATOR_MARKER_GROUP_NAME);
    if (!group) {
      group = new THREE.Group();
      group.name = ACTUATOR_MARKER_GROUP_NAME;
      scene.add(group);
    }

    cleanupActuatorMarkerGroup(group);
    group.visible = !!showActuators;

    let rafId = null;
    if (!showActuators) {
      return () => {
        cleanupActuatorMarkerGroup(group);
        if (group.parent) group.parent.remove(group);
      };
    }

    const modelGroup = scene.getObjectByName("modelGroup");
    const markerScale = computeConstraintMarkerScaleFromModelGroup(modelGroup);
    const markerRadius = CONSTRAINT_MARKER_RADIUS * markerScale * 1.25;
    const routeObjectIndex = buildViewerSceneRouteObjectIndex(scene);
    const anchorCounts = new Map();

    Object.values(actuators || {}).forEach((actuator) => {
      if (!actuator?.uuid) return;

      let anchor = null;
      let targetKind = null;
      let targetId = "";
      const jointUuid =
        typeof actuator.joint === "string"
          ? resolveJointUuidFromRef(actuator.joint, safeJointMapObject || {})
          : null;

      if (jointUuid) {
        const vizGroup = findJointVizGroupInScene(scene, jointUuid);
        if (vizGroup) {
          anchor = { kind: "joint", vizGroup };
          targetKind = "joint";
          targetId = jointUuid;
        }
      }

      if (!anchor) {
        const tendon = resolveTendonForActuator(actuator, allTendons || {});
        anchor = getTendonAnchor(tendon, scene, routeObjectIndex);
        if (anchor && tendon?.uuid) {
          targetKind = "tendon";
          targetId = tendon.uuid;
        }
      }

      if (!anchor) return;

      const anchorKey = `${targetKind || anchor.kind}:${targetId || actuator.uuid}`;
      const anchorOffsetIndex = anchorCounts.get(anchorKey) || 0;
      anchorCounts.set(anchorKey, anchorOffsetIndex + 1);

      const marker = createMarkerSphere(markerRadius);
      marker.name = `actuator_marker_${actuator.uuid}`;
      marker.renderOrder = 10004;
      marker.userData = {
        isActuatorVisualization: true,
        isActuatorMarker: true,
        actuatorUuid: actuator.uuid,
        actuatorName: actuator.name || "",
        anchorKind: anchor.kind,
        anchorOffsetIndex,
        targetKind,
        targetId,
        vizGroup: anchor.vizGroup || null,
        routeObject: anchor.routeObject || null,
      };
      updateMarkerPosition(marker, jointObjectsRef);

      if (labelsVisible) {
        const onSelect = (event) => {
          event.preventDefault();
          event.stopPropagation();
          const selection = useSelectionStore.getState();
          if (targetKind === "joint") {
            selection.selectActuatorInParent(actuator.uuid, targetId, null);
          } else if (targetKind === "tendon") {
            selection.selectActuatorInParent(actuator.uuid, null, targetId);
          } else {
            selection.selectActuator(actuator.uuid);
          }
          selection.setLastSelectionSource("scene");
        };
        const labelDiv = createActuatorLabelElement({ actuator, labelsVisible, onSelect });
        const label = new CSS2DObject(labelDiv);
        applyMarkerLabelCss2dLayout(label);
        label.position.set(0, CONSTRAINT_MARKER_LABEL_LIFT_LOCAL_METERS, 0);
        label.userData = {
          isActuatorVisualization: true,
          isActuatorMarkerLabel: true,
          actuatorUuid: actuator.uuid,
          stackSpreadSortKey: `actuator:${String(actuator.uuid)}`,
          stackSpreadBaseLocal: label.position.clone(),
        };
        marker.add(label);
      }
      group.add(marker);
    });

    const tick = () => {
      if (group.visible) {
        group.children.forEach((child) => updateMarkerPosition(child, jointObjectsRef));
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      cleanupActuatorMarkerGroup(group);
      if (group.parent) group.parent.remove(group);
    };
  }, [
    actuators,
    allTendons,
    jointObjectsRef,
    labelsVisible,
    safeJointMapObject,
    sceneObjects,
    sceneRef,
    showActuators,
    structureChangeTick,
  ]);
}
